import React, { useEffect, useRef } from 'react';
import logoUrl from '../components/assests/logoblack.png';

const HeroParticles = ({ isActive }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef({ logoTokens: [], flowingDots: [], continuousInflow: [] });
  const globalAlphaRef = useRef(0);
  const formationTimerRef = useRef(0);
  const sdfRef = useRef(null);
  // logoblack.png ships at 1024x1024; rasterizing the SDF close to that
  // native resolution (rather than a 2x downsample) keeps the tightest
  // curve in the mark — the small inner hook — from blurring out.
  const offSize = 1024;

  // Strict Aesthetic Configuration
  const SPACING = 10; // Controls identical distance between dots everywhere
  const DOT_SIZE = 2.3; // 2px - 3px
  const COLOR = '#1f3a8a';
  const FORMATION_DURATION = 3500; // Time spent flying in the initial logo batch
  const INFLOW_COUNT = 5; // How many particles randomly join later

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Helper: spawn at a random edge
    const getRandomEdgePoint = (width, height) => {
      const edge = Math.floor(Math.random() * 4);
      let x, y;
      if (edge === 0) { x = -20; y = Math.random() * height; } // left
      else if (edge === 1) { x = width + 20; y = Math.random() * height; } // right
      else if (edge === 2) { x = Math.random() * width; y = -20; } // top
      else { x = Math.random() * width; y = height + 20; } // bottom
      
      return { x, y };
    };

    const calculateGrid = () => {
      if (!sdfRef.current) return;
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Responsive Rule: Compute logo scale based on screen bounds
      let logoScale = Math.min(width * 0.38, 520);
      if (width < 768) {
        logoScale = Math.min(width * 0.5, 300); // Give it slightly more proportion on tight mobile
      }

      // Responsive X Positioning
      let centerX;
      if (width < 768) {
        centerX = width * 0.5; // Centered on mobile
      } else if (width <= 1024) {
        const t = (width - 768) / 256; 
        centerX = width * (0.5 + 0.2 * t); // Pans gracefully from 0.5 to 0.7
      } else {
        centerX = width * 0.7; // Locked nicely to the right on desktop
      }
      
      const centerY = height * 0.5;

      const logoTokens = [];
      const flowingDots = [];
      const sdf = sdfRef.current;

      const sampleSDF = (u, v) => {
        const px = (u + 1) * 0.5 * (offSize - 1);
        const py = (v + 1) * 0.5 * (offSize - 1);
        let ix = Math.floor(px);
        let iy = Math.floor(py);
        if (ix < 0) ix = 0;
        if (iy < 0) iy = 0;
        if (ix > offSize - 2) ix = offSize - 2;
        if (iy > offSize - 2) iy = offSize - 2;
        const fx = px - ix;
        const fy = py - iy;
        const s00 = sdf[iy * offSize + ix];
        const s10 = sdf[iy * offSize + (ix + 1)];
        const s01 = sdf[(iy + 1) * offSize + ix];
        const s11 = sdf[(iy + 1) * offSize + (ix + 1)];
        return s00 * (1 - fx) * (1 - fy) + s10 * fx * (1 - fy) + s01 * (1 - fx) * fy + s11 * fx * fy;
      };

      const halfGridCount = Math.floor((logoScale / 2) / SPACING);

      for (let i = -halfGridCount; i <= halfGridCount; i++) {
        for (let j = -halfGridCount; j <= halfGridCount; j++) {
          const u = i / halfGridCount;
          const v = j / halfGridCount;

          if (sampleSDF(u, v) <= 0) {
            const targetX = centerX + i * SPACING;
            const targetY = centerY + j * SPACING;

            logoTokens.push({ targetX, targetY });

            const edge = prefersReducedMotion
              ? { x: targetX, y: targetY }
              : getRandomEdgePoint(width, height);
            flowingDots.push({
               x: edge.x,
               y: edge.y,
               targetX,
               targetY,
               // Floor kept high enough that even a dot spawned at the
               // farthest corner still arrives within FORMATION_DURATION,
               // instead of straggling in over many extra seconds.
               speed: Math.random() * 0.018 + 0.032,
               settled: prefersReducedMotion
            });
          }
        }
      }
      
      particlesRef.current = { 
        logoTokens, 
        flowingDots, 
        continuousInflow: [] 
      };
    };

    const setCanvasSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);

      calculateGrid();

      // The grid just changed shape; if the loop had gone idle, wake it
      // back up so the resized layout actually gets drawn.
      if (idle) {
        idle = false;
        inflowStartedAt = null;
        lastTime = performance.now();
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    const generateSDF = (imgData) => {
      const INF = 1e20;
      const w = offSize;
      const h = offSize;
      
      const edt1d = (f, d, n, v, z) => {
          let k = 0;
          v[0] = 0;
          z[0] = -INF;
          z[1] = INF;
          for (let q = 1; q < n; q++) {
              let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
              while (s <= z[k]) {
                  k--;
                  s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
              }
              k++;
              v[k] = q;
              z[k] = s;
              z[k + 1] = INF;
          }
          k = 0;
          for (let q = 0; q < n; q++) {
              while (z[k + 1] < q) k++;
              d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
          }
      };

      const computeEDT2D = (grid) => {
          const f = new Float64Array(w);
          const d = new Float64Array(w);
          const v = new Int32Array(w);
          const z = new Float64Array(w + 1);

          for (let x = 0; x < w; x++) {
              for (let y = 0; y < h; y++) f[y] = grid[y * w + x];
              edt1d(f, d, h, v, z);
              for (let y = 0; y < h; y++) grid[y * w + x] = d[y];
          }
          for (let y = 0; y < h; y++) {
              for (let x = 0; x < w; x++) f[x] = grid[y * w + x];
              edt1d(f, d, w, v, z);
              for (let x = 0; x < w; x++) grid[y * w + x] = d[x];
          }
      };

      const grid_in = new Float64Array(w * h);
      const grid_out = new Float64Array(w * h);
      for (let i = 0; i < w * h; i++) {
          const alpha = imgData[i * 4 + 3];
          if (alpha > 128) {
              grid_out[i] = 0;
              grid_in[i] = INF;
          } else {
              grid_out[i] = INF;
              grid_in[i] = 0;
          }
      }

      computeEDT2D(grid_out);
      computeEDT2D(grid_in);

      const sdf = new Float64Array(w * h);
      for (let i = 0; i < w * h; i++) {
          sdf[i] = Math.sqrt(grid_out[i]) - Math.sqrt(grid_in[i]);
      }
      return sdf;
    };

    const loadAndInit = () => {
      const img = new Image();
      img.src = logoUrl;
      img.onload = () => {
        const offCanvas = document.createElement('canvas');
        offCanvas.width = offSize;
        offCanvas.height = offSize;
        const offCtx = offCanvas.getContext('2d');
        offCtx.drawImage(img, 0, 0, offSize, offSize);
        
        const imgData = offCtx.getImageData(0, 0, offSize, offSize).data;
        sdfRef.current = generateSDF(imgData);
        calculateGrid();
      };
      
      img.onerror = () => {
        calculateGrid();
      };
    };

    window.addEventListener('resize', setCanvasSize);
    
    // Initial setup
    const dimsRef = { width: window.innerWidth, height: window.innerHeight };
    
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.scale(dpr, dpr);
    loadAndInit();

    let lastTime = performance.now();
    const INFLOW_ACTIVE_DURATION = 9000; // stop spawning new inflow dots after this long
    let inflowStartedAt = null;
    let idle = false;

    const animate = (time) => {
      const delta = time - lastTime;
      lastTime = time;
      const dt = Math.min(delta / 16.66, 2);

      // Keep dims updated correctly for the edge-spawn reference every frame without state binding
      dimsRef.width = window.innerWidth;
      dimsRef.height = window.innerHeight;

      ctx.clearRect(0, 0, dimsRef.width, dimsRef.height);

      if (isActive) {
        if (globalAlphaRef.current < 1) {
          globalAlphaRef.current += 0.005 * dt; 
        }
        formationTimerRef.current += delta;
      }

      const alpha = Math.min(globalAlphaRef.current, 1);

      if (alpha > 0) {
        const { logoTokens, flowingDots, continuousInflow } = particlesRef.current;

        // --- LAYER 1: No scattered background at all. Empty and pristine. ---

        // --- LAYER 2: Fly-in Logo Particles ---
        // These are the initial ~600-800 dots forming the shape.
        ctx.fillStyle = COLOR;
        
        let allSettled = true;

        flowingDots.forEach((p) => {
          if (!p.settled) {
            allSettled = false;
            const dx = p.targetX - p.x;
            const dy = p.targetY - p.y;
            
            p.x += dx * p.speed * dt;
            p.y += dy * p.speed * dt;

            // Arrive
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 0.5) {
              p.settled = true;
              p.x = p.targetX;
              p.y = p.targetY;
            }
          }

          ctx.beginPath();
          ctx.arc(p.x, p.y, DOT_SIZE, 0, Math.PI * 2);
          
          // Completely opaque if settled, slight twinkle if flying
          ctx.globalAlpha = p.settled ? (1 * alpha) : (1 * alpha); 
          ctx.fill();
        });

        // --- LAYER 3: Continuous Merge Particles ---
        // Wait until initial formation is mostly over, AND make sure we don't have too many.
        // This runs for a limited window only, then the scene goes fully static.
        if (allSettled && formationTimerRef.current > FORMATION_DURATION) {
          if (inflowStartedAt === null) inflowStartedAt = time;
          const inflowElapsed = time - inflowStartedAt;

          // Spawn missing inflow particles up to INFLOW_COUNT (e.g., 5-6)
          while (
            !prefersReducedMotion &&
            inflowElapsed < INFLOW_ACTIVE_DURATION &&
            continuousInflow.length < INFLOW_COUNT &&
            logoTokens.length > 0
          ) {
             const edge = getRandomEdgePoint(dimsRef.width, dimsRef.height);
             
             // Pick a random ALREADY SETTLED coordinate on the logo
             const targetNode = logoTokens[Math.floor(Math.random() * logoTokens.length)];
             
             continuousInflow.push({
                x: edge.x,
                y: edge.y,
                targetX: targetNode.targetX,
                targetY: targetNode.targetY,
                // These merge slowly and elegantly
                speed: Math.random() * 0.005 + 0.006, 
             });
          }
        }

        // Draw Inflow Particles
        for (let i = continuousInflow.length - 1; i >= 0; i--) {
           const p = continuousInflow[i];
           
           const dx = p.targetX - p.x;
           const dy = p.targetY - p.y;
           
           p.x += dx * p.speed * dt;
           p.y += dy * p.speed * dt;
           
           ctx.beginPath();
           ctx.arc(p.x, p.y, DOT_SIZE, 0, Math.PI * 2);
           ctx.globalAlpha = 1 * alpha;
           ctx.fill();

           // Once it reaches the logo, it simply "absorbs" (disappears into the already present solid dot).
           // We splice it out so a new one spawns next frame!
           const dist = Math.sqrt(dx * dx + dy * dy);
           if (dist < 1) {
              continuousInflow.splice(i, 1);
           }
        }

        ctx.globalAlpha = 1;

        // Once the logo has fully formed and the brief inflow window has
        // finished, the scene is static: stop redrawing every frame.
        const inflowWindowOver =
          inflowStartedAt !== null && time - inflowStartedAt >= INFLOW_ACTIVE_DURATION;
        if (allSettled && inflowWindowOver && continuousInflow.length === 0) {
          idle = true;
        }
      }

      if (!idle) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    // Dots are pre-settled at their target positions when reduced motion is
    // preferred (see calculateGrid), so this only ever fades opacity in and
    // then goes idle. No inflow spawns, no positional movement.
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Strictly no mouse interaction
        zIndex: 0,
      }}
    />
  );
};

export default HeroParticles;
